import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["clientSearch", "searchResults", "selectedServices", "step", "staffList"]
  static values = { 
    currentStep: Number,
    selectedService: Object,
    selectedStaff: String,
    clientData: Object
  }
  
  connect() {
    this.currentStepValue = 1
    this.selectedServiceValue = null
    this.selectedStaffValue = null
    this.clientDataValue = {}
  }
  
  searchClients(event) {
    const query = event.target.value
    if (query.length > 2) {
      this.performClientSearch(query)
    } else {
      this.hideSearchResults()
    }
  }
  
  performClientSearch(query) {
    fetch(`/operations/clients/search?q=${encodeURIComponent(query)}`, {
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.displaySearchResults(data.clients, query)
    })
    .catch(error => {
      console.error('Client search failed:', error)
    })
  }
  
  displaySearchResults(clients, query) {
    const resultsHtml = clients.map(client => 
      `<div class="p-2 border-b border-gray-200 hover:bg-gray-50 cursor-pointer" data-action="click->quick-booking#selectClient" data-client-id="${client.id}">
        <p class="font-medium text-gray-900">${client.name}</p>
        <p class="text-sm text-gray-600">${client.phone} • Last visit: ${client.last_visit}</p>
      </div>`
    ).join('')
    
    const addNewHtml = `<div class="p-2 hover:bg-gray-50 cursor-pointer" data-action="click->quick-booking#addNewClient" data-client-name="${query}">
      <p class="font-medium text-blue-600">+ Add "${query}" as new client</p>
    </div>`
    
    this.searchResultsTarget.innerHTML = resultsHtml + addNewHtml
    this.showSearchResults()
  }
  
  selectClient(event) {
    const clientId = event.currentTarget.dataset.clientId
    const clientName = event.currentTarget.querySelector('p').textContent
    
    this.clientDataValue = { id: clientId, name: clientName }
    this.clientSearchTarget.value = clientName
    this.hideSearchResults()
  }
  
  addNewClient(event) {
    const clientName = event.currentTarget.dataset.clientName
    this.clientDataValue = { name: clientName, new: true }
    this.clientSearchTarget.value = clientName
    this.hideSearchResults()
  }
  
  selectService(event) {
    const service = event.currentTarget.dataset.service
    const duration = event.currentTarget.dataset.duration
    const price = event.currentTarget.querySelector('p:last-child').textContent.split('•')[1].trim()
    
    this.selectedServiceValue = { 
      type: service, 
      duration: parseInt(duration),
      price: price
    }
    
    this.updateSelectedServices()
    this.highlightSelectedService(event.currentTarget)
    this.loadAvailableStaff()
  }
  
  removeService() {
    this.selectedServiceValue = null
    this.selectedServicesTarget.classList.add("hidden")
    this.clearServiceHighlights()
  }
  
  selectStaff(event) {
    const staffId = event.currentTarget.dataset.staffId
    this.selectedStaffValue = staffId
    this.highlightSelectedStaff(event.currentTarget)
  }
  
  confirmBooking(event) {
    event.preventDefault()
    
    if (this.validateBooking()) {
      this.submitBooking()
    } else {
      this.showValidationErrors()
    }
  }
  
  validateBooking() {
    return this.clientDataValue && 
           this.selectedServiceValue && 
           (this.selectedStaffValue || document.querySelector('#auto-assign').checked)
  }
  
  submitBooking() {
    const form = this.element.querySelector('form')
    const formData = new FormData(form)
    
    // Add our custom data
    if (this.clientDataValue.name) {
      formData.set('appointment[client_name]', this.clientDataValue.name)
    }
    if (this.clientDataValue.phone || formData.get('appointment[client_phone]')) {
      formData.set('appointment[client_phone]', this.clientDataValue.phone || formData.get('appointment[client_phone]'))
    }
    if (this.selectedServiceValue) {
      formData.set('appointment[service_type]', this.selectedServiceValue.type)
      formData.set('appointment[duration]', this.selectedServiceValue.duration)
    }
    if (this.selectedStaffValue) {
      formData.set('appointment[staff_id]', this.selectedStaffValue)
    }
    
    // Set as walk-in appointment
    formData.set('appointment[appointment_type]', 'walk_in')
    formData.set('appointment[start_time]', new Date().toISOString())
    
    fetch('/operations/appointments', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.showSuccessMessage()
        this.closeBooking()
      } else {
        this.showErrorMessage(data.errors)
      }
    })
    .catch(error => {
      console.error('Booking submission failed:', error)
      this.showErrorMessage(['An error occurred while creating the booking'])
    })
  }
  
  loadAvailableStaff() {
    if (!this.selectedServiceValue) return
    
    fetch(`/operations/staff/available?service=${this.selectedServiceValue.type}`, {
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.updateStaffList(data.staff)
    })
    .catch(error => {
      console.error('Failed to load available staff:', error)
    })
  }
  
  updateStaffList(staff) {
    // Update the staff list with available staff for the selected service
    const staffHtml = staff.map(member => `
      <div class="p-3 border-2 border-green-500 bg-green-50 rounded-lg cursor-pointer" data-action="click->quick-booking#selectStaff" data-staff-id="${member.id}">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <p class="font-medium text-gray-900">${member.name}</p>
              <p class="text-sm text-gray-600">Available now • ${member.specialty || 'All Services'}</p>
            </div>
          </div>
          <div class="text-right">
            <p class="text-sm font-medium text-green-600">Available</p>
            <p class="text-xs text-gray-500">Can start now</p>
          </div>
        </div>
      </div>
    `).join('')
    
    const existingStaffCards = this.staffListTarget.querySelectorAll('[data-staff-id]')
    existingStaffCards.forEach(card => card.remove())
    
    if (staffHtml) {
      this.staffListTarget.insertAdjacentHTML('afterbegin', staffHtml)
    }
  }
  
  close() {
    const modalContainer = document.querySelector('[data-operations-layout-target="modalContainer"]')
    if (modalContainer) {
      modalContainer.classList.add('hidden')
      modalContainer.innerHTML = ''
      document.body.classList.remove('overflow-hidden')
    }
  }
  
  closeBooking() {
    this.close()
  }
  
  cancel() {
    this.close()
  }
  
  // Helper methods
  showSearchResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.remove("hidden")
    }
  }
  
  hideSearchResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.add("hidden")
    }
  }
  
  updateSelectedServices() {
    if (this.selectedServiceValue && this.hasSelectedServicesTarget) {
      const serviceInfo = this.selectedServicesTarget.querySelector('div')
      serviceInfo.innerHTML = `
        <div>
          <p class="font-medium text-blue-900">Selected: ${this.selectedServiceValue.type}</p>
          <p class="text-sm text-blue-700">${this.selectedServiceValue.duration} minutes • ${this.selectedServiceValue.price}</p>
        </div>
      `
      this.selectedServicesTarget.classList.remove("hidden")
    }
  }
  
  highlightSelectedService(element) {
    // Remove previous selections
    element.parentElement.querySelectorAll('button').forEach(btn => {
      btn.classList.remove('border-blue-500', 'bg-blue-50')
      btn.classList.add('border-gray-200')
    })
    
    // Highlight selected
    element.classList.remove('border-gray-200')
    element.classList.add('border-blue-500', 'bg-blue-50')
  }
  
  highlightSelectedStaff(element) {
    // Remove previous selections
    element.parentElement.querySelectorAll('[data-staff-id]').forEach(card => {
      card.classList.remove('border-blue-500', 'bg-blue-50')
      card.classList.add('border-green-500', 'bg-green-50')
    })
    
    // Highlight selected
    element.classList.remove('border-green-500', 'bg-green-50')
    element.classList.add('border-blue-500', 'bg-blue-50')
  }
  
  clearServiceHighlights() {
    document.querySelectorAll('[data-service]').forEach(btn => {
      btn.classList.remove('border-blue-500', 'bg-blue-50')
      btn.classList.add('border-gray-200')
    })
  }
  
  showSuccessMessage() {
    // Implementation for success notification
    const notification = document.createElement('div')
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50'
    notification.textContent = 'Booking created successfully!'
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
  
  showErrorMessage(errors) {
    // Implementation for error notification
    const errorText = Array.isArray(errors) ? errors.join(', ') : errors
    const notification = document.createElement('div')
    notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50'
    notification.textContent = `Error: ${errorText}`
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 5000)
  }
  
  showValidationErrors() {
    let message = 'Please complete all required fields:'
    if (!this.clientDataValue || !this.clientDataValue.name) {
      message += '\n• Client name'
    }
    if (!this.selectedServiceValue) {
      message += '\n• Service selection'
    }
    if (!this.selectedStaffValue && !document.querySelector('#auto-assign').checked) {
      message += '\n• Staff assignment'
    }
    
    this.showErrorMessage(message)
  }
}